import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { linkedOrderOptionLabel, linkRowCandidates } from "@/lib/tt/linkedOrders";
import type { LinkableOrder } from "@/lib/tt/linkedOrders";
import type { LinkedOrderEntry } from "./types";

interface LinkedOrderFieldProps {
  isEditable: boolean;
  links: LinkedOrderEntry[];
  /**
   * Every order that can be linked, ranked best-match-first by
   * `rankLinkedOrderCandidates`. Each row narrows this to the orders no other
   * row has taken.
   */
  candidateOrders: LinkableOrder[];
  error?: string;
  onChangeLink: (index: number, guidOrderId: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}

/**
 * Link rows for the order being edited. Options are labelled with the
 * instrument and free-text fields the ranking is based on, so the order that
 * was floated to the top shows why it matched.
 */
export function LinkedOrderField({
  isEditable,
  links,
  candidateOrders,
  error,
  onChangeLink,
  onAdd,
  onRemove,
}: LinkedOrderFieldProps) {
  // Every candidate is already claimed by a row, so a new row would have
  // nothing to offer.
  const nothingLeftToAdd =
    linkRowCandidates(candidateOrders, links.map((entry) => entry.guid_order_id)).length === 0;

  return (
    <div className="space-y-2">
      <Label>
        Linked Order(s) <span className="text-destructive">*</span>
      </Label>

      <div className="space-y-2">
        {links.map((entry, index) => {
          // An order may only be linked once, so a row hides what its siblings
          // already hold. Its own choice stays selectable so the picker can
          // still show the label of what it currently holds.
          const chosenByOtherRows = links
            .filter((_, i) => i !== index)
            .map((other) => other.guid_order_id);
          const options = linkRowCandidates(candidateOrders, chosenByOtherRows);

          return (
            <div key={entry.key} className="flex gap-2 items-start">
              <div className="flex-1">
                <Combobox
                  disabled={!isEditable}
                  dwimOnChange
                  isModal={false}
                  placeholder={options.length > 0 ? "Select order to link..." : "No other orders"}
                  options={options.map((order) => ({
                    value: order.guid_order_id,
                    label: linkedOrderOptionLabel(order),
                  }))}
                  value={entry.guid_order_id}
                  virtualizeList
                  className="w-full justify-between"
                  popoverClassName="w-[560px] h-[300px]"
                  onValueChange={(val) => onChangeLink(index, val)}
                />
              </div>
              {links.length > 1 && (
                <Button
                  disabled={!isEditable}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(index)}
                  className="h-9 px-2 text-destructive hover:text-destructive"
                >
                  ✕
                </Button>
              )}
            </div>
          );
        })}

        <Button
          disabled={!isEditable || nothingLeftToAdd}
          type="button"
          variant="outline"
          size="sm"
          onClick={onAdd}
          className="mt-1"
        >
          + Add Link
        </Button>
      </div>

      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}
